export interface Category {
  slug: string;
  name: string;
  en: string;
  blurb: string;
  tagline: string;
  cover: string;
  accent: string;
}

export interface Article {
  id: string;
  slug: string;
  title: string;
  categorySlug: string;
  cover: string;
  excerpt: string;
  date: string;
  readTime: string;
  author: string;
  featured?: boolean;
  tags: string[];
  paragraphs: string[];
}

export const categories: Category[] = [
  {
    slug: "lishi-miwen",
    name: "历史秘闻",
    en: "LORE & ARCHIVE",
    blurb: "正史之外的角落，藏着一部更生动的野史。档案、笔记、口述与史官删改之处，都是线索的入口。",
    tagline: "被写进正文的未必是全部",
    cover:
      "https://readdy.ai/api/search-image?query=aged%20yellowed%20Chinese%20manuscripts%20and%20bamboo%20slips%20scattered%20on%20a%20dark%20scholar%20desk%20lit%20by%20warm%20candlelight%2C%20brush%20and%20inkstone%20nearby%2C%20golden%20sepia%20amber%20tones%2C%20mysterious%20archival%20atmosphere%2C%20painterly%20editorial%20photography%2C%20high%20detail%2C%20deep%20shadows&width=1000&height=700&seq=cat-miwen&orientation=landscape",
    accent: "amber",
  },
  {
    slug: "kaogu-faxian",
    name: "考古发现",
    en: "FIELD DISCOVERY",
    blurb: "探方里的一锹一刷，都可能改写一段记忆。从现场到实验室，追踪遗物如何开口说话。",
    tagline: "土地比史书更诚实",
    cover:
      "https://readdy.ai/api/search-image?query=archaeologists%20carefully%20brushing%20ancient%20pottery%20shards%20in%20a%20layered%20excavation%20trench%2C%20warm%20afternoon%20golden%20light%2C%20soil%20strata%20visible%2C%20field%20archaeology%20scene%2C%20earthy%20ochre%20and%20amber%20tones%2C%20documentary%20style%20photography%2C%20high%20detail&width=1000&height=700&seq=cat-kaogu&orientation=landscape",
    accent: "ochre",
  },
  {
    slug: "gumu-tanmi",
    name: "古墓探秘",
    en: "TOMB EXPEDITION",
    blurb: "石门之后的光阴凝固成另一种秩序。跟随考古视角，走进墓室、耳室与墓道深处的工匠世界。",
    tagline: "石门背后自有其语言",
    cover:
      "https://readdy.ai/api/search-image?query=narrow%20dark%20ancient%20tomb%20entrance%20with%20worn%20stone%20steps%20descending%20into%20shadow%2C%20a%20single%20warm%20lantern%20glow%2C%20carved%20stone%20doorframe%2C%20burnt%20umber%20and%20charcoal%20tones%2C%20mysterious%20cinematic%20atmosphere%2C%20deep%20contrast%2C%20painterly%20editorial%20photography&width=1000&height=700&seq=cat-gumu&orientation=landscape",
    accent: "ember",
  },
  {
    slug: "lishi-mituan",
    name: "历史谜团",
    en: "ENIGMA FILE",
    blurb: "有些问题在千年后才重新有了坐标。民间传说、舆图与实物相互印证，谜团也会渐渐显形。",
    tagline: "真正的谜底往往近在眼前",
    cover:
      "https://readdy.ai/api/search-image?query=antique%20Chinese%20map%20with%20cryptic%20red%20markings%20and%20a%20brass%20compass%20on%20weathered%20wood%2C%20warm%20candlelight%2C%20rolled%20scrolls%20beside%2C%20rust%20and%20amber%20tones%2C%20mystery%20investigation%20mood%2C%20editorial%20product%20photography%2C%20high%20detail&width=1000&height=700&seq=cat-mituan&orientation=landscape",
    accent: "rust",
  },
];

export const articles: Article[] = [
  {
    id: "a1",
    slug: "fenshu-yujin",
    title: "焚书余烬：一场大火之后，失传古籍如何重见天日",
    categorySlug: "lishi-miwen",
    cover:
      "https://readdy.ai/api/search-image?query=charred%20ancient%20book%20fragments%20and%20burnt%20bamboo%20slips%20rescued%20from%20ash%2C%20scholar%20hands%20restoring%20paper%20under%20warm%20lamplight%2C%20amber%20and%20smoky%20sepia%20tones%2C%20preservation%20workshop%20scene%2C%20documentary%20style%2C%20high%20detail%2C%20atmospheric&width=1200&height=800&seq=art-fenshu&orientation=landscape",
    excerpt:
      "一部被认为早已失传的古籍，竟在后世藏家的故纸堆里留下残页。当竹简、帛书与抄本相互补全，被时间撕碎的文本如何重新拼成完整的故事。",
    date: "2026-08-18",
    readTime: "12 分钟",
    author: "闻鹫",
    featured: true,
    tags: ["古籍流传", "版本考据", "禁书"],
    paragraphs: [
      "在历代文献目录中，这部书始终只有一个名字和一个缺笔的注脚，仿佛作者刻意把自己的痕迹留在删改之处。研究者们在各家书目与藏书印中反复比对，终于在一册不起眼的抄本夹页里，找到了与传世目录严丝合缝的三行补录。",
      "失传的关键往往不在战火，而在后来的每一次‘重编’。古人抄书时常自行取舍，把不合时宜的段落整段略去。今天能读到的所谓定本，有时反而是删减最彻底的版本——这让寻找‘另一版’成了考据中最迷人的部分。",
      "当散落各地的残页按版式、笔迹与避讳逐一归位，一部被大火与偏见共同掩埋的著作，终于以它本来的模样重新开口。这提醒我们，历史很少真正消失，它只是改换了藏身之处。",
    ],
  },
  {
    id: "a2",
    slug: "chenmo-zhaoshu",
    title: "沉默的诏书：一件被错判了百年的文物",
    categorySlug: "lishi-miwen",
    cover:
      "https://readdy.ai/api/search-image?query=aged%20imperial%20edict%20scroll%20with%20vermilion%20seal%20on%20a%20dim%20wooden%20desk%2C%20yellowed%20silk%2C%20warm%20candlelight%2C%20magnifying%20lens%20resting%20beside%2C%20amber%20and%20cinnabar%20tones%2C%20investigation%20mood%2C%20editorial%20photography%2C%20high%20detail&width=1200&height=800&seq=art-zhaoshu&orientation=landscape",
    excerpt:
      "一件流传了百年的诏书，纸墨、印泥与形制都对得上，却被普遍当作后人的仿品。直到一位年轻学者注意到敕令里一个不起眼的用字，真相才被重新打量。",
    date: "2026-08-05",
    readTime: "9 分钟",
    author: "青崖",
    featured: true,
    tags: ["鉴定辨伪", "宫廷文书"],
    paragraphs: [
      "文物辨伪最忌先入为主。这件诏书因‘纸色过新’长期被压在库房角落，可一旦把它放回文书流转的实际环境，所谓破绽便经不起推敲——抄录副本本就要用新纸，这与是否官方出品的判断并无关系。",
      "转机来自印章下压着的一个称谓。宫廷文书对皇帝的称呼有极严格的时令，错一个时期便对不上号。研究者顺着这个称谓，把诏书落款的年份往前推了整整一代，此前所有矛盾竟逐一化解。",
      "一纸判词的反复，折射的正是鉴定学的方法论之争：证据该服务于结论，还是结论必须服从证据。当执念放下，沉默的诏书才第一次被真正听见。",
    ],
  },
  {
    id: "a3",
    slug: "gongqiang-banye",
    title: "宫墙深处：被史官删去的半页真相",
    categorySlug: "lishi-miwen",
    cover:
      "https://readdy.ai/api/search-image?query=shadowed%20ancient%20palace%20corridor%20at%20dusk%20with%20lanterns%20and%20carved%20wooden%20beams%2C%20a%20half%20open%20door%20casting%20light%2C%20crimson%20pillars%20and%20amber%20glow%2C%20mysterious%20atmosphere%2C%20cinematic%20editorial%20photography%2C%20high%20detail%2C%20warm%20contrast&width=1200&height=800&seq=art-gongqiang&orientation=landscape",
    excerpt:
      "同一桩宫廷旧事，在实录、国史与私家笔记中呈现出三种面貌。哪一页被删去，恰恰泄露了最不该被记住的那部分。",
    date: "2026-07-22",
    readTime: "10 分钟",
    author: "闻鹫",
    featured: true,
    tags: ["宫廷史", "史料对勘"],
    paragraphs: [
      "把三种记载并列时，差异立刻显现：官方实录用两行匆匆带过，国史则干脆改变了事件的因果顺序，只有私家笔记保留了当事人的只言片语。史官未必说谎，但他们有权选择‘不说’。",
      "有趣的是，删改留下的痕迹本身也会说话。一处明显断裂的叙事、一个前后矛盾的日期，都像路标般指向被抹去的那半页。研究者常说，读史不仅要读写下的，更要读没写下的。",
      "当我们把笔记中的私语放回那个具体的时间与制度里，一段被刻意剪平的往事才重新显出棱角。真相未必在某一册里，它往往横跨在文本与文本的空隙之中。",
    ],
  },
  {
    id: "a4",
    slug: "huangtu-chengbang",
    title: "黄土之下的城邦：一处青铜时代的意外发现",
    categorySlug: "kaogu-faxian",
    cover:
      "https://readdy.ai/api/search-image?query=aerial%20view%20of%20an%20ancient%20city%20wall%20foundation%20emerging%20from%20excavation%20pits%20in%20golden%20afternoon%20light%2C%20survey%20grids%20and%20trowels%2C%20archaeological%20dig%20site%2C%20warm%20earthy%20tones%2C%20documentary%20photography%2C%20high%20detail&width=1200&height=800&seq=art-chengbang&orientation=landscape",
    excerpt:
      "一次为修路而做的例行勘探，竟在地表三米之下摸到一面绵延数百米的夯土城墙。沉睡多年的早期聚落，轮廓逐渐清晰。",
    date: "2026-08-27",
    readTime: "11 分钟",
    author: "陆离",
    featured: false,
    tags: ["聚落考古", "早期文明"],
    paragraphs: [
      "勘探队的探铲在第三米处忽然受阻，带出的土色从褐变黄、由松变实——这是人工夯土的典型信号。随后的布方揭露印证了判断：一段保存完好的城墙基址出现在探沟里，两侧还夹着排水沟的遗迹。",
      "城址的意义不止于‘找到了墙’。通过对墙体夯层、墙芯夹木与灰坑里陶片的整理，研究者重建出这座聚落数百年的扩张轨迹：它先是一处小型聚落，后筑墙成城，最终又因河流改道而衰落。",
      "意外发现提醒我们，这片土地的记忆远比我们以为的完整。真正需要警惕的，从来不是‘挖不到’，而是‘因为没预期，所以没细看’。",
    ],
  },
  {
    id: "a5",
    slug: "liujin-shoumian",
    title: "鎏金兽面：一件被误认的‘祭祀神器’",
    categorySlug: "kaogu-faxian",
    cover:
      "https://readdy.ai/api/search-image?query=gleaming%20gilded%20bronze%20animal%20mask%20ornament%20on%20dark%20stone%20background%2C%20warm%20spotlight%2C%20textured%20gold%20and%20copper%20patina%2C%20museum%20artifact%20photography%2C%20dramatic%20amber%20lighting%2C%20high%20detail%2C%20cinematic&width=1200&height=800&seq=art-shoumian&orientation=landscape",
    excerpt:
      "它曾被摆进展柜当作祭祀重器，直到修复时在背面发现几处不起眼的铆痕。一件所谓‘神器’的真实身份，也随之被改写。",
    date: "2026-07-30",
    readTime: "8 分钟",
    author: "青崖",
    tags: ["器物研究", "功能考释"],
    paragraphs: [
      "对功能模糊的器物，考古学最忌‘以貌取义’。这件鎏金兽面体型规整、纹样繁复，天然适合被解读为礼制重器——可它正面被反复打磨的痕迹，反而暗示着更日常的用途。",
      "修复师在清理氧化层时发现的背面铆痕是关键。它们分布均匀、受力明确，说明这件器物原本要固定在某个木质载体上，而非独立陈设。结合同时期建筑构件，研究者重新把它归入车舆装饰一类。",
      "一个称呼的更替，看似轻描淡写，实则让器物从‘高不可攀的神坛’回到真实可感的物质生活。历史里最动人的，往往不是最隆重的那一面。",
    ],
  },
  {
    id: "a6",
    slug: "jiandu-xiexie",
    title: "一枚简牍，如何改写了一部地方志",
    categorySlug: "kaogu-faxian",
    cover:
      "https://readdy.ai/api/search-image?query=rows%20of%20ancient%20bamboo%20slips%20with%20inked%20characters%20on%20a%20wooden%20table%20under%20soft%20warm%20light%2C%20calligraphy%20brush%20and%20ink%20stone%2C%20scholar%20research%20scene%2C%20golden%20sepia%20tones%2C%20editorial%20photography%2C%20high%20detail&width=1200&height=800&seq=art-jiandu&orientation=landscape",
    excerpt:
      "地方志里关于某座古渡口的记载只有一句话。而汉墓出土的数百枚简牍，却让这句孤证变成了可以触摸的县治图景。",
    date: "2026-07-11",
    readTime: "9 分钟",
    author: "陆离",
    tags: ["简牍研究", "区域史"],
    paragraphs: [
      "简牍的价值在于‘碎片成史’。单看一枚，不过几行文书；可当数百枚按户籍、赋税、邮驿分类拼接，一个县级社会的日常便显影了。研究者据此重新判定了那处古渡口的位置与兴衰。",
      "最惊喜的是一枚记干支与邮程的简。它精确标出了相邻两驿之间的里程，恰好与传世地方志中模糊的‘若干里’相互参差。借助这枚简，原本不可考的驿路走向，第一次有了实据。",
      "一个具体个案被改写，背后是方法论的一次胜利：当文献的概括与出土材料的细节相遇，历史研究便不再只依赖文人的一支笔。",
    ],
  },
  {
    id: "a7",
    slug: "youshen-ershi",
    title: "幽深耳室：打开一座‘空’墓之后",
    categorySlug: "gumu-tanmi",
    cover:
      "https://readdy.ai/api/search-image?query=archaeologists%20entering%20a%20dark%20stone%20tomb%20chamber%20lit%20by%20headlamps%2C%20dust%20in%20beam%20of%20light%2C%20empty%20stone%20sarcophagus%20platform%2C%20cold%20shadow%20and%20warm%20lamp%2C%20cinematic%20documentary%2C%20high%20detail%2C%20amber%20and%20charcoal%20tones&width=1200&height=800&seq=art-ershi&orientation=landscape",
    excerpt:
      "墓门完好、封土未动，主室里却空空如也。当考古队把目光转向不起眼的耳室，一座‘空墓’的秘密才开始揭晓。",
    date: "2026-09-01",
    readTime: "13 分钟",
    author: "闻鹫",
    featured: true,
    tags: ["墓葬制度", "盗墓考古"],
    paragraphs: [
      "空墓往往比满墓更复杂。主棺位置有清晰的抬动痕迹，说明墓主曾在此下葬；陪葬品却被‘干净’地搬走，连碎片都少之又少——这不像仓促的破坏，更像一次有组织、有路线的移葬。",
      "耳室里清理出的几枚铁钉与残木，把答案引向另一个方向：这或许不是盗扰，而是当时流行的‘迁葬’。古人相信，把先人遗骸迁往更妥帖的风水之地，是孝道的延伸。封土未动，只因入口根本不在正面。",
      "当‘空墓’被重新解释为‘他处另有归宿’，考古工作并未结束，而是开始了新的追寻——顺着志怪方志里的只言片语，研究者或许能在地图上的某个村庄，找到遗骸真正的安放之所。",
    ],
  },
  {
    id: "a8",
    slug: "jiguan-gongjiang",
    title: "防盗机关背后：古代工匠的另一种智慧",
    categorySlug: "gumu-tanmi",
    cover:
      "https://readdy.ai/api/search-image?query=ancient%20stone%20trap%20mechanism%20and%20sliding%20stone%20door%20block%20inside%20a%20dim%20tomb%2C%20wooden%20beam%20and%20rope%20details%2C%20warm%20lantern%20light%2C%20engineering%20detail%2C%20amber%20tones%2C%20documentary%20photography%2C%20high%20detail&width=1200&height=800&seq=art-jiguan&orientation=landscape",
    excerpt:
      "史书把墓中机关渲染得玄之又玄。真正走近这些石门、伏火与积沙设计，会发现它们并非神术，而是一整套理性应对盗掘的工程学。",
    date: "2026-07-05",
    readTime: "8 分钟",
    author: "陆离",
    tags: ["营造工艺", "防盗设计"],
    paragraphs: [
      "积沙墓之所以难盗，原理其实朴素：在棺椁四周填入大量细沙，盗洞一旦挖入，沙即随洞倾泻，将盗洞反复封死。它不是传说，而是对泥土物理的精准利用。",
      "那些被描绘为‘弩箭自射’的机关，在实物中更多表现为结构性防御——厚石门、封门石与回填的土层。考古学更愿意相信，真正有效的防盗，是让盗墓者‘无路可走’，而非制造一次性的惊险。",
      "把机关还原为工程，并不减损古人的智慧，反而让智慧更可敬：他们没有依赖超自然，而是把对材料的理解、对盗掘套路的观察，凝练成一整套可以复制的营造经验。",
    ],
  },
  {
    id: "a9",
    slug: "bihua-tuise",
    title: "壁画褪色之前：我们为何争分夺秒地记录",
    categorySlug: "gumu-tanmi",
    cover:
      "https://readdy.ai/api/search-image?query=faded%20ancient%20tomb%20murals%20of%20figures%20and%20deer%20on%20a%20wall%2C%20conservator%20using%20digital%20documentation%20device%2C%20warm%20dim%20light%2C%20cinnabar%20and%20ochre%20pigments%2C%20restoration%20scene%2C%20documentary%20photography%2C%20high%20detail&width=1200&height=800&seq=art-bihua&orientation=landscape",
    excerpt:
      "色彩见光即变，颜料一触即落。对壁画的保护，首先是一场与时间的赛跑——在褪色之前，把它完整地记录下来。",
    date: "2026-08-12",
    readTime: "9 分钟",
    author: "青崖",
    featured: true,
    tags: ["壁画保护", "数字考古"],
    paragraphs: [
      "壁画进入墓室的瞬间便已开始倒计时：湿度变化让盐分析出，光线让有机颜料缓慢分解。考古工作里最紧迫的一环，不是‘挖出什么’，而是‘在褪色前留住什么’。",
      "数字记录因此成为保护的核心手段。多光谱成像能在肉眼察觉变化前，捕捉颜料层的原始信息；高精度三维扫描则把每一处笔触的位置固定下来，为后代研究者留下‘此刻’的完整快照。",
      "保存技术无法让时间停住，却能把它拉长。今天的每一次细致记录，都是未来博物馆里无法估价的底稿——真正的守护，始于看见它正在消逝。",
    ],
  },
  {
    id: "a10",
    slug: "shigu-liangming",
    title: "同一具尸骨，两个朝代的名字",
    categorySlug: "lishi-mituan",
    cover:
      "https://readdy.ai/api/search-image?query=forensic%20archaeologist%20examining%20ancient%20human%20remains%20with%20warm%20lab%20lighting%2C%20osteology%20tools%20and%20documentation%2C%20scholarly%20scene%2C%20amber%20tones%2C%20documentary%20photography%2C%20high%20detail%2C%20serious%20atmosphere&width=1200&height=800&seq=art-shigu&orientation=landscape",
    excerpt:
      "一座墓里的尸骨，却被两个不同朝代的地方志分别记为‘本地名人’。同位素、族谱与葬俗的共同推理，让这场‘认领’尘埃落定。",
    date: "2026-06-28",
    readTime: "11 分钟",
    author: "青崖",
    tags: ["人骨考古", "文献互证"],
    paragraphs: [
      "名人墓之争，本质是地方叙事之争。两本方志各自把这座坟写进‘乡贤’，时间却相差一个朝代。要判归属，必须放下纸面，先问骨头自己怎么说。",
      "碳十四测年划定了下葬的年代区间；对牙齿釉质的锶同位素分析，则揭示了此人幼年时期的地理来源。两者共同收窄了范围，让其中一方文献记载与遗骸特征显著背离。",
      "当科学证据与族谱世系、葬俗制度相互闭合，那座墓的主人终于有了相对可信的归属。谜团的解开，靠的不是某一条压倒性的证据，而是让所有线索都指向同一处。",
    ],
  },
  {
    id: "a11",
    slug: "xiaoshi-yizhan",
    title: "消失的驿站：驿路考古把目光投向哪里",
    categorySlug: "lishi-mituan",
    cover:
      "https://readdy.ai/api/search-image?query=overgrown%20ancient%20stone%20road%20and%20tumbled%20way%20station%20ruins%20in%20golden%20hills%2C%20wild%20grass%20and%20worn%20wheel%20ruts%2C%20warm%20afternoon%20sun%2C%20rust%20and%20amber%20tones%2C%20landscape%20archaeology%2C%20documentary%20photography%2C%20high%20detail&width=1200&height=800&seq=art-yizhan&orientation=landscape",
    excerpt:
      "诗里反复出现的古驿站，为何从舆图上集体消失？驿路考古正沿着车辙、地名与残碑，一点点拼出早已改道的古代交通网。",
    date: "2026-06-16",
    readTime: "10 分钟",
    author: "陆离",
    tags: ["交通史", "地名考古"],
    paragraphs: [
      "驿站的消失多半不是因为‘被毁’，而是因为‘改道’。当主干交通线因河道变迁或政治中心转移而东移，沿途驿站便迅速失去意义，退化成普通的村落，甚至彻底荒废。",
      "考古上寻找驿站，常借力于那些‘死而不僵’的地名：许多村庄至今仍沿用‘某某铺’‘某某驿’的旧称，保存着几百年前交通节点的记忆。再配合残碑、车轮磨出的石槽与遗物，驿站便重新显影。",
      "一张完整的驿路网，是理解古代行政与商贸的骨架。当我们在荒草中找到它遗留的一角，消失的并非一个地名，而是一整套连接帝国末梢的神经。",
    ],
  },
  {
    id: "a12",
    slug: "jingzhong-jingying",
    title: "井中镜影：民间传说里藏着的真实坐标",
    categorySlug: "lishi-mituan",
    cover:
      "https://readdy.ai/api/search-image?query=old%20stone%20well%20at%20a%20quiet%20ancient%20courtyard%20at%20dusk%2C%20mossy%20stones%20and%20wooden%20shutter%2C%20single%20lantern%20reflecting%20in%20water%2C%20mysterious%20amber%20tones%2C%20cinematic%20editorial%20photography%2C%20high%20detail%2C%20quiet%20suspense&width=1200&height=800&seq=art-jingzhong&orientation=landscape",
    excerpt:
      "一则关于‘井底映出旧城’的乡野传说，听起来荒诞不经。可当研究者把它与地下管沟的工程记录对照，竟发现传说并非空穴来风。",
    date: "2026-05-30",
    readTime: "8 分钟",
    author: "闻鹫",
    tags: ["民间传说", "城市考古"],
    paragraphs: [
      "民间传说常被当作不可靠的谈资，但它在传递时往往保留了真实的空间信息——只是被夸张与移花接木包裹着。只要找到那个‘坐标点’，荒诞便有了入口。",
      "这则传说围绕一口老井展开，说井水曾照见‘地下的街市’。研究者在地质钻孔与市政管道的工程资料中，果然在井位附近发现了一条被掩埋的旧河道与砖铺路面，与传说中的方向惊人一致。",
      "传说的价值，不在于它‘说了什么’，而在于它‘指了哪里’。把口述放回地面，用物证去校准——许多看似怪诞的旧谈，其实都是被时间扭曲的实地记录。",
    ],
  },
];

export const site = {
  name: "猎奇网",
  slogan: "掀开历史的封土，聆听地下的回响",
  intro:
    "猎奇网是一档专注历史秘闻与考古发现的中文内容站。我们从正史边角、古墓现场与民间旧谈出发，结合史料与实地发现，追踪文物背后的真相，还原那些被时间掩埋的古代奇事。",
  contact: {
    email: "story@lieqiwang.example",
    weibo: "猎奇历史探秘",
  },
};

export const aboutFaqs = [
  {
    q: "猎奇网的历史内容来自哪里？",
    a: "我们的选题大多源自传世文献、地方志、考古简报与公开学术成果，并在此基础上进行通俗化转述。涉及实地发现时，会优先引用考古发掘报告的结论。",
  },
  {
    q: "文章里讲的传说都是真实发生过的吗？",
    a: "我们会严格区分‘史料记载’与‘民间传说’。对于尚无实据的旧谈，文中会明确标注其流传背景与可能对应的历史原型，不渲染成确凿事实。",
  },
  {
    q: "看到喜欢的栏目，如何持续跟进？",
    a: "你可以在栏目页按主题订阅，或在首页底部留下邮箱。编辑部会在新的一期‘探秘简报’发布时，把精选内容第一时间送达。",
  },
  {
    q: "我读到的历史细节和教科书不太一样，是错误吗？",
    a: "未必是错误。史学本就存在不同解读，尤其是被史料删改的角落。我们尽可能在同一篇内并列多种说法并标注来源，方便你自己比对判断。",
  },
];